/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { DashboardGrid } from 'carbon-addons-iot-react';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compact from 'lodash/compact';

import { DataItemsPropTypes } from '../../constants/constants';
import { dateSelectorMessages } from '../../../Dashboard/components/DashboardDateSelector/DashboardDateSelector';

import CardRenderer from './CardRenderer';

const messages = defineMessages({
  noDataLabel: 'No data is available for this time range.',
  noDataShortLabel: 'No data',
  errorLoadingLabel: 'Error loading data for this card. Refresh the page to load the data: ',
  errorLoadingShortLabel: 'Data error',
  // card labels
  rollingPeriodLabel: 'Rolling period',
  lastHourLabel: 'Last hour',
  last2HoursLabel: 'Last 2 hours',
  last4HoursLabel: 'Last 4 hours',
  last8HoursLabel: 'Last 8 hours',
  last24HoursLabel: 'Last 24 hours',
  last7DaysLabel: 'Last 7 days',
  lastMonthLabel: 'Last month',
  lastQuarterLabel: 'Last quarter',
  lastYearLabel: 'Last year',
  periodToDateLabel: 'Period to date',
  defaultLabel: 'Default',
  this24HoursLabel: 'This 24 hours',
  thisWeekLabel: 'This week',
  thisMonthLabel: 'This month',
  thisQuarterLabel: 'This quarter',
  thisYearLabel: 'This year',
  hourlyLabel: 'Hourly',
  dailyLabel: 'Daily',
  weeklyLabel: 'Weekly',
  monthlyLabel: 'Monthly',
  expandLabel: 'Expand to fullscreen',
  closeLabel: 'Close',
  selectTimeRangeLabel: 'Select time range',
  alertDetected: 'Alert detected: ',
  overflowMenuDescription: 'open and close list of options',
  tooltipGroupLabel: 'Group',
  tooltipTotalLabel: 'Total',
  // card actions
  editCardLabel: 'Edit card',
  cloneCardLabel: 'Clone card',
  deleteCardLabel: 'Delete card',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  zoomToFit: 'Minimize',
  // table card labels
  criticalLabel: 'Critical',
  moderateLabel: 'Moderate',
  lowLabel: 'Low',
  selectSeverityPlaceholder: 'Select',
  severityLabel: 'Severity',
  searchPlaceholder: 'Search',
  filterButtonAria: 'Filters',
  defaultFilterStringPlaceholdText: 'Enter filter',
  downloadIconDescription: 'Download table content',
  clearAllFilters: 'Clear all filters',
  itemsRangeWithTotal: '{min}–{max} of {total} items',
  pageRange: '{current} of {total} pages',
  openMenuAria: 'Open menu',
  closeMenuAria: 'Close menu',
  clearSelectionAria: 'Clear selection',
  // icon label
  noIconFound: 'The icon name {name} is not valid.',
  emptyMessageWithFilters: 'No results match the current filters',
  emptyMessage: 'There is no data for this time range.',
});

/**
 * This component renders one individual dashboard from an array of properties. The passed cards are set into a grid layout based on the individual card sizes and layouts.
 * It keeps track of the current breakpoint and passes it down to the rendered cards.
 * This dashboard only supports a fixed set of card types and uses the CardRenderer to make those determinations
 * If you want to render a custom card type you should compose your own dashboard component with the DashboardHeader, DashboardGrid, and Card components.
 *
 * Data Loading:
 * It keeps track of whether any cards are actively loading data and shows a loading spinner at the top.
 * It listens to all the cards data fetching, and updates it's overall refresh date once all cards have finished fetching data.
 * To enable your cards to fetch data, you must implement the onFetchData callback.  The callback is called with the full card prop object,
 * and then a boolean that describes whether to return timeseries data or not.  You should asynchronously return an array of values from your callback to populate your
 * cards with data.
 */
const CommonDashboard = ({
  intl: { formatMessage },
  cards,
  sidebar,
  // eslint-disable-next-line react/prop-types
  layouts,
  isEditable,
  onLayoutChange,
  onBreakpointChange,
  className,
  isLoading,
  setIsLoading,
  hasInstanceDashboard,
  deviceId,
  // TODO: remove onSetRefresh and instead listen to setIsLoading
  onSetRefresh,
  onCardRender,
  onFetchData,
  timeGrain,
  hasTimeGrainChanged,
  dataItems,
  entityType,
  filter,
}) => {
  const i18n = useMemo(
    () => ({
      /* card labels */
      noDataLabel: formatMessage(messages.noDataLabel),
      noDataShortLabel: formatMessage(messages.noDataShortLabel),
      errorLoadingDataLabel: formatMessage(messages.errorLoadingLabel),
      errorLoadingDataShortLabel: formatMessage(messages.errorLoadingShortLabel),
      // date labels
      rollingPeriodLabel: formatMessage(messages.rollingPeriodLabel),
      lastHourLabel: formatMessage(messages.lastHourLabel),
      last2HoursLabel: formatMessage(messages.last2HoursLabel),
      last4HoursLabel: formatMessage(messages.last4HoursLabel),
      last8HoursLabel: formatMessage(messages.last8HoursLabel),
      last24HoursLabel: formatMessage(messages.last24HoursLabel),
      last7DaysLabel: formatMessage(messages.last7DaysLabel),
      lastMonthLabel: formatMessage(messages.lastMonthLabel),
      lastQuarterLabel: formatMessage(messages.lastQuarterLabel),
      lastYearLabel: formatMessage(messages.lastYearLabel),
      periodToDateLabel: formatMessage(messages.periodToDateLabel),
      this24HoursLabel: formatMessage(messages.this24HoursLabel),
      thisWeekLabel: formatMessage(messages.thisWeekLabel),
      thisMonthLabel: formatMessage(messages.thisMonthLabel),
      thisQuarterLabel: formatMessage(messages.thisQuarterLabel),
      thisYearLabel: formatMessage(messages.thisYearLabel),
      defaultLabel: formatMessage(messages.defaultLabel),
      hourlyLabel: formatMessage(messages.hourlyLabel),
      dailyLabel: formatMessage(messages.dailyLabel),
      weeklyLabel: formatMessage(messages.weeklyLabel),
      monthlyLabel: formatMessage(messages.monthlyLabel),
      // new date range picker labels
      toLabel: formatMessage(dateSelectorMessages.to),
      toNowLabel: formatMessage(dateSelectorMessages.toNow),
      calendarLabel: formatMessage(dateSelectorMessages.calendar),
      customRangeLinkLabel: formatMessage(dateSelectorMessages.customRange),
      customRangeLabel: formatMessage(dateSelectorMessages.customRange),
      relativeLabel: formatMessage(dateSelectorMessages.relative),
      lastLabel: formatMessage(dateSelectorMessages.last),
      invalidNumberLabel: formatMessage(dateSelectorMessages.invalidNumber),
      relativeToLabel: formatMessage(dateSelectorMessages.relativeTo),
      absoluteLabel: formatMessage(dateSelectorMessages.absolute),
      startTimeLabel: formatMessage(dateSelectorMessages.startTime),
      endTimeLabel: formatMessage(dateSelectorMessages.endTime),
      applyBtnLabel: formatMessage(dateSelectorMessages.applyBtn),
      cancelBtnLabel: formatMessage(dateSelectorMessages.cancelBtn),
      backBtnLabel: formatMessage(dateSelectorMessages.backBtn),
      increment: formatMessage(dateSelectorMessages.increment),
      decrement: formatMessage(dateSelectorMessages.decrement),
      hours: formatMessage(dateSelectorMessages.hours),
      minutes: formatMessage(dateSelectorMessages.minutes),
      number: formatMessage(dateSelectorMessages.number),
      intervalLabels: [
        formatMessage(dateSelectorMessages.minutes),
        formatMessage(dateSelectorMessages.hours),
        formatMessage(dateSelectorMessages.days),
        formatMessage(dateSelectorMessages.weeks),
        formatMessage(dateSelectorMessages.months),
        formatMessage(dateSelectorMessages.years),
      ],
      relativeLabels: [
        formatMessage(dateSelectorMessages.today),
        formatMessage(dateSelectorMessages.yesterday),
      ],
      // additional labels
      overflowMenuDescription: formatMessage(messages.overflowMenuDescription),
      alertDetected: formatMessage(messages.alertDetected),
      zoomIn: formatMessage(messages.zoomIn),
      zoomOut: formatMessage(messages.zoomOut),
      zoomToFit: formatMessage(messages.zoomToFit),
      // card action labels
      selectTimeRangeLabel: formatMessage(messages.selectTimeRangeLabel),
      expandLabel: formatMessage(messages.expandLabel),
      closeLabel: formatMessage(messages.closeLabel),
      editCardLabel: formatMessage(messages.editCardLabel),
      cloneCardLabel: formatMessage(messages.cloneCardLabel),
      deleteCardLabel: formatMessage(messages.deleteCardLabel),
      criticalLabel: formatMessage(messages.criticalLabel),
      moderateLabel: formatMessage(messages.moderateLabel),
      lowLabel: formatMessage(messages.lowLabel),
      selectSeverityPlaceholder: formatMessage(messages.selectSeverityPlaceholder),
      severityLabel: formatMessage(messages.severityLabel),
      downloadIconDescription: formatMessage(messages.downloadIconDescription),
      searchPlaceholder: formatMessage(messages.searchPlaceholder),
      filterButtonAria: formatMessage(messages.filterButtonAria),
      defaultFilterStringPlaceholdText: formatMessage(messages.defaultFilterStringPlaceholdText),
      clearAllFilters: formatMessage(messages.clearAllFilters),
      emptyButtonLabelWithFilters: formatMessage(messages.clearAllFilters),
      emptyMessageWithFilters: formatMessage(messages.emptyMessageWithFilters),
      emptyMessage: formatMessage(messages.emptyMessage),
      itemsRangeWithTotal: (min, max, total) =>
        formatMessage(messages.itemsRangeWithTotal, { min, max, total }),
      pageRange: (current, total) => formatMessage(messages.pageRange, { current, total }),
      openMenuAria: formatMessage(messages.openMenuAria),
      closeMenuAria: formatMessage(messages.closeMenuAria),
      clearSelectionAria: formatMessage(messages.clearSelectionAria),
      tooltipGroupLabel: formatMessage(messages.tooltipGroupLabel),
      tooltipTotalLabel: formatMessage(messages.tooltipTotalLabel),
    }),
    [formatMessage]
  );

  const [breakpoint, setBreakpoint] = useState('lg');

  // Keep track of whether any cards are loading or not, (doesn't need to be in state)
  const cardsLoadingRef = useRef([]);

  // Setup the loading tracker for the cards if the dashboard decides to load
  useEffect(
    () => {
      if (isLoading) {
        onSetRefresh(null);
      } else {
        cardsLoadingRef.current = []; // after we finish loading reset the counter
        if (onSetRefresh) {
          onSetRefresh(Date.now());
        }
      }
    },
    [isLoading] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Listen to the card fetches to determine whether all cards have finished loading
  const handleDataFetched = useCallback(
    id => {
      if (cardsLoadingRef.current && !cardsLoadingRef.current.includes(id)) {
        cardsLoadingRef.current.push(id);
      }
      // If the card array count matches the card count, we call setIsLoading to false, and clear the array
      if (cardsLoadingRef.current.length === cards.length && setIsLoading) {
        setIsLoading(false);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const cachedOnBreakpointChange = useCallback(
    newBreakpoint => {
      setBreakpoint(newBreakpoint);
      if (onBreakpointChange) {
        onBreakpointChange(newBreakpoint);
      }
    },
    [onBreakpointChange]
  );

  // Uses our shared renderer for each card that knows how to render a fixed set of card types
  const gridContents = useMemo(
    () =>
      compact(
        cards?.map(card =>
          card ? (
            <CardRenderer
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...card}
              entityType={entityType}
              key={`renderer-${card.id}`}
              i18n={i18n}
              isDashboardLoading={isLoading}
              isEditable={isEditable}
              breakpoint={breakpoint}
              onCardRender={onCardRender}
              onFetchData={onFetchData}
              onDataFetched={handleDataFetched}
              hasDashboardTimeGrainChanged={hasTimeGrainChanged}
              dashboardTimeGrain={timeGrain}
              hasInstanceDashboard={hasInstanceDashboard}
              deviceId={deviceId}
              dataItems={dataItems}
              filter={filter}
            />
          ) : null
        )
      ),
    [
      cards,
      entityType,
      i18n,
      isLoading,
      isEditable,
      breakpoint,
      onFetchData,
      onCardRender,
      handleDataFetched,
      hasTimeGrainChanged,
      timeGrain,
      hasInstanceDashboard,
      deviceId,
      dataItems,
      filter,
    ]
  );

  return (
    <div className={classNames('common-dashboard', className)}>
      <div className="common-dashboard-grid">
        {sidebar && <div className="common-dashboard-grid-sidebar">{sidebar}</div>}
        <DashboardGrid
          supportedLayouts={['lg', 'md', 'sm']}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          isEditable={isEditable}
          breakpoint={breakpoint}
          onBreakpointChange={cachedOnBreakpointChange}>
          {gridContents}
        </DashboardGrid>
      </div>
    </div>
  );
};

CommonDashboard.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      values: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
      /** is the card actively loading, it will override the dashboard loading state if true */
      isLoading: PropTypes.bool,
      /** was there an error loading */
      error: PropTypes.node,
    })
  ).isRequired,

  /** Optional sidebar content that should be rendered left of the dashboard cards */
  sidebar: PropTypes.node,

  // Callback functions
  /** Callback called when a card should fetch its data, called with the card props and a boolean that determines whether a card supports timeseries data or not.  Return a promise that returns the updated card object with values.  It will be passed downstream to your card as props to update. */
  onFetchData: PropTypes.func,
  /** Optional Function that is called before the card renders, allows you to add images or any data not in the cardConfig */
  onCardRender: PropTypes.func,

  // Data related properties
  /** If the overall dashboard should be using a timeGrain, we pass it here */
  timeGrain: PropTypes.string,
  /** has the user updated the timegrain of the dashboard */
  hasTimeGrainChanged: PropTypes.bool,
  /** Property that will trigger all cards to load again */
  isLoading: PropTypes.bool,
  /** once all the cards have finished loading this will be called */
  setIsLoading: PropTypes.func,
  isEditable: PropTypes.bool,
  onLayoutChange: PropTypes.func,
  onBreakpointChange: PropTypes.func,
  onSetRefresh: PropTypes.func,
  className: PropTypes.string,
  hasInstanceDashboard: PropTypes.bool,
  deviceId: PropTypes.string,
  /** the card data mapper needs the entityType to map the drilldown links */
  entityType: PropTypes.string,
  /** the card mapper needs these dataItems to figure out how to format the values */
  dataItems: DataItemsPropTypes,
  filter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

CommonDashboard.defaultProps = {
  sidebar: null,
  onCardRender: null,
  dataItems: [],
  entityType: null,
  onFetchData: null,
  timeGrain: null,
  hasTimeGrainChanged: false,
  isLoading: false,
  setIsLoading: null,
  isEditable: false,
  onLayoutChange: null,
  onBreakpointChange: null,
  onSetRefresh: null,
  className: null,
  hasInstanceDashboard: false,
  deviceId: null,
  filter: null,
};

export default injectIntl(CommonDashboard);
